// src/hooks/useOrders.js
import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import apiClient from "../common/http.service";

export const useOrders = () => {
    const [orders, setOrders] = useState([]);
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ totalPages: 1, currentPage: 1, totalCount: 0 });

    // Fetch all orders with pagination & filters
    const fetchOrders = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const response = await apiClient.get("/orders", params);
            setOrders(response.data.data || response.data);
            setPagination({
                totalPages: response.data.totalPages || 1,
                currentPage: response.data.currentPage || 1,
                totalCount: response.data.count || 0,
            });
        } catch (error) {
            toast.error(error.message || "Failed to fetch orders");
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch a single order by ID
    const fetchOrderById = useCallback(async (id) => {
        setLoading(true);
        try {
            const response = await apiClient.get(`/orders/${id}`);
            setOrder(response.data.data || response.data);
        } catch (error) {
            toast.error(error.message || "Failed to fetch order details");
        } finally {
            setLoading(false);
        }
    }, []);

    // Update order status (e.g., Pending -> Shipped)
    const updateOrderStatus = async (id, status) => {
        try {
            const response = await apiClient.put(`/orders/${id}/status`, { status });
            toast.success("Order status updated!");
            
            // Update local state so UI reflects changes instantly
            if (order && order._id === id) {
                setOrder(prev => ({ ...prev, status }));
            }
            setOrders(prev => prev.map(o => o._id === id ? { ...o, status } : o));
            
            return response.data;
        } catch (error) {
            toast.error(error.message || "Failed to update status");
            throw error;
        }
    };
   const deleteOrderById = async (id) => {
        if (!window.confirm("Are you sure you want to delete this order? This action cannot be undone.")) return;
        
        try {
            await apiClient.delete(`/orders/${id}`);
            toast.success("Order deleted successfully!");
            // Remove the deleted order from the local state
            setOrders(prev => prev.filter(o => o._id !== id));
            // Update total count
            setPagination(prev => ({ ...prev, totalCount: prev.totalCount - 1 }));
        } catch (error) {
            toast.error(error.message || "Failed to delete order");
        }
    };

    return {
        orders,
        order,
        loading,
        pagination,
        fetchOrders,
        fetchOrderById,
        updateOrderStatus,
        deleteOrderById // <-- Export the new function
    };
};